import { useEffect, useRef, useState } from "react";
import { UploadSimple, TextT, Trash, FileText, Books, Anchor, BookOpen, DownloadSimple, ArrowsClockwise, Wrench, Info, PencilSimple, TextAa, CaretDown, CaretRight, MagnifyingGlass, X, CheckSquare, Square, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ConfirmModal, PromptModal } from "@/components/PromptModals";
import TierPickerModal from "@/components/TierPickerModal";
import DocEditorModal from "@/components/DocEditorModal";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { TierArtCard } from "@/components/TierArtCard";
import { useSiloArt } from "@/components/SiloArtLayer";
// Jul 2026 (Randy): Library-tab Distill/Refine Silo + the "Refinery Jobs" bucket
// were removed. Refining is self-contained on the Refinery tab now.

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

const TIER_META = {
  1: {
    label: "Tier 1 · The Anchor",
    icon: Anchor,
    accent: "#4A5D4E",
    weight: "1.5",
    tooltip: "Your identity core — who you are, what you believe, what you always want said. It works one of two ways and you choose which. FULL INJECTION sends every Tier 1 file with every single question: nothing is searched, so nothing can be missed, but the whole body rides in every prompt. CORE LIBRARY sends only the files you pin Always On and searches the rest in larger passages, so one long argument can be reached at depth instead of padding every prompt. Either way the index is built automatically and costs nothing — only Core Library searches it. Caps either way: 300 KB per file, 1 MB per tier.",
    perDocKB: 300, perSiloKB: 1000,
  },
  2: {
    label: "Tier 2 · The Logic Core",
    icon: BookOpen,
    accent: "#1C1B1A",
    weight: "1.0",
    tooltip: "Heavy-weighted retrieval. The RAG system searches Tier 2 first when answering. Per-doc cap 1 MB, per-silo cap 10 MB. For very long source material, run it through the Refinery first — a focused summary often answers questions better than the raw transcript.",
    perDocKB: 1000, perSiloKB: 10000,
  },
  3: {
    label: "Tier 3 · The Library",
    icon: Books,
    accent: "#6B6A68",
    weight: "0.5",
    tooltip: "The deepest archive — only consulted when Tiers 1 and 2 can't answer. Per-doc cap 5 MB, per-silo cap 50 MB. Refinery can distill a giant file into something more searchable; Workbench can split one huge doc into focused topical chunks.",
    perDocKB: 5000, perSiloKB: 50000,
  },
};

// Oct 6 2026 — the Always On fill bar EDUCATES, it does not enforce. Randy's
// ruling: "I want the system to educate using char count, token count,
// estimated cost — not enforce a philosophy we have not measured yet." Past
// this many chars the bar simply changes colour. No warning text, no block.
const ALWAYS_ON_ADVISORY_CHARS = 150_000;
const fmtCost = (cents) => {
  const c = Number(cents || 0);
  if (c <= 0) return "$0";
  if (c < 1) return `$${(c / 100).toFixed(4)}`;
  return `$${(c / 100).toFixed(3)}`;
};

const fmtKB = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${(bytes / 1_000).toFixed(0)} KB`;
  return `${bytes} B`;
};
const fmtChars = (n) => (n || 0).toLocaleString();

function TierZone({ tier, onUpload, onPaste, busy, usage, t1, onModeChange, modeBusy, onReindex, reindexJob, artOn }) {
  const fileRef = useRef(null);
  const [pasting, setPasting] = useState(false);
  const [pasteTitle, setPasteTitle] = useState("");
  const [pasteContent, setPasteContent] = useState("");
  const meta = TIER_META[tier];
  const Icon = meta.icon;
  const coreMode = tier === 1 && t1?.mode === "core_library";

  const submitPaste = async () => {
    if (!pasteContent.trim()) return toast.error("Paste content first.");
    await onPaste({ title: pasteTitle || "Untitled passage", content: pasteContent });
    setPasteTitle(""); setPasteContent(""); setPasting(false);
  };

  // Capacity meter math (Feb 24, 2026)
  const used = usage?.used_bytes || 0;
  const cap = usage?.per_silo_cap_bytes || (meta.perSiloKB * 1000);
  const pct = Math.min(100, Math.round((used / cap) * 100));
  const meterColor = pct >= 90 ? "#C16E5A" : pct >= 70 ? "#C19A4A" : meta.accent;
  const pasteOversized = pasteContent
    ? (new Blob([pasteContent]).size > (meta.perDocKB * 1000))
    : false;

  // Oct 16, 2026 (Randy) — with Silo Art on (stage 1 or 2) the three tier
  // cards become the painted version. Art off returns this original card
  // exactly as it was, which is where the weight badge, the Always-On cost
  // meter, the index-build prompt, the rights line and the paste counter all
  // continue to live.
  if (artOn) {
    return (
      <TierArtCard
        tier={tier}
        meta={meta}
        tooltip={<TierTooltip text={meta.tooltip} testid={`tier-tooltip-${tier}`} onArt />}
        usedLabel={fmtKB(used)}
        capLabel={fmtKB(cap)}
        pct={pct}
        coreMode={coreMode}
        onModeChange={onModeChange}
        modeBusy={modeBusy}
        onUpload={onUpload}
        onPaste={onPaste}
        busy={busy}
      />
    );
  }

  return (
    <div className="bg-white border hairline rounded-lg p-4 flex flex-col" data-testid={`tier-zone-${tier}`}>
      {/* iter-35e: condensed mobile-friendly layout — tighter padding, icon
          and title on a single row so 3 cards stack neatly without dominating
          the viewport. */}
      <div className="flex items-center gap-1.5 mb-3">
        <div className="w-7 h-7 rounded-md bg-[#F0F1EE] flex items-center justify-center shrink-0" style={{ color: meta.accent }}>
          <Icon size={14} weight="duotone" />
        </div>
        <div className="text-sm font-medium text-[#1C1B1A] flex items-center gap-1.5 flex-1 min-w-0">
          <span className="truncate">{meta.label}</span>
          {/* iter-35f: tooltip popup positioned ABOVE the (i) icon, centered.
              Uses delayed-close grace period to prevent flicker when the
              cursor crosses the gap to read the popup. */}
          <TierTooltip text={meta.tooltip} testid={`tier-tooltip-${tier}`} />
        </div>
        {/* Oct 6 2026 (Randy pt-tier1-two-mode) — retrieval-behaviour badge.
            Tier 1's is a live two-state control (the ONLY tier with a choice);
            Tier 2/3 state what they already do. The weight badge beside it is
            DISPLAY-ONLY on every tier: it communicates each layer's role, and
            Randy is "not yet convinced those numbers should be editable". */}
        {tier === 1 ? (
          <button
            type="button"
            onClick={() => onModeChange?.(coreMode ? "full_injection" : "core_library")}
            disabled={modeBusy}
            data-testid="tier1-mode-toggle"
            data-mode={coreMode ? "core_library" : "full_injection"}
            title={coreMode
              ? "Tier 1 is a searchable Core Library: only the files you pin Always On ride in every prompt, the rest are searched. Tap to switch to Full Injection."
              : "Tier 1 is Full Injection: every file is sent with every question, nothing is searched. Tap to switch to Core Library (searched, with Always On pins)."}
            className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 transition-colors disabled:opacity-50 text-center leading-tight ${
              coreMode
                ? "border-[#4A5D4E] bg-[#EDF2EE] text-[#4A5D4E]"
                : "border-[#C19A4A] bg-[#FCF6E6] text-[#8A6D1F]"
            }`}
          >
            <span className="block">{coreMode ? "Vector/RAG" : "Full Injection"}</span>
            <span className="block text-[8px] uppercase tracking-wide opacity-60">Choose injection</span>
          </button>
        ) : (
          <span className="text-[10px] px-1.5 py-0.5 rounded border hairline bg-[#F7F7F4] text-[#6B6A68] shrink-0" data-testid={`tier-${tier}-retrieval-badge`}>
            Vector/RAG
          </span>
        )}
        <span
          className="text-[10px] px-1.5 py-0.5 rounded border hairline bg-white text-[#1C1B1A] shrink-0 tabular-nums"
          title="Relative weight of this layer in an answer. Display only."
          data-testid={`tier-${tier}-weight-badge`}
        >
          {meta.weight}
        </span>
      </div>

      {/* Oct 6 2026 — Core Library mode: the meter reports the ALWAYS ON
          selection against the same 1 MB ceiling Full Injection uses, so the
          difference between "all of it" and "the files I chose" is visible at a
          glance, in chars, tokens and real per-answer cost. */}
      {coreMode ? (
        <div className="mb-3" data-testid="tier-1-always-on-meter">
          <div className="flex justify-between text-[10px] text-[#6B6A68] mb-1 gap-2">
            <span className="truncate">
              Always On <span className="opacity-60">({fmtChars(t1?.always_on_chars)} chars · ~{fmtChars(t1?.always_on_tokens)} tokens)</span>
            </span>
            <span
              className="shrink-0 tabular-nums"
              style={{ color: (t1?.always_on_chars || 0) > ALWAYS_ON_ADVISORY_CHARS ? "#C19A4A" : meta.accent }}
              data-testid="tier-1-always-on-cost"
            >
              {fmtCost(t1?.always_on_cost_cents_per_ask)}/answer
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-[#F0F1EE] overflow-hidden">
            <div
              className="h-full"
              style={{
                // Floor of 2% so a deliberate small selection still reads as
                // "something is pinned" rather than an empty bar.
                width: `${(t1?.always_on_chars || 0) > 0
                  ? Math.max(2, Math.min(100, Math.round(((t1.always_on_chars) / (meta.perSiloKB * 1000)) * 100)))
                  : 0}%`,
                background: (t1?.always_on_chars || 0) > ALWAYS_ON_ADVISORY_CHARS ? "#C19A4A" : meta.accent,
                transition: "width 200ms",
              }}
            />
          </div>
          <div className="text-[10px] text-[#6B6A68] mt-1 leading-snug" data-testid="tier-1-core-summary">
            {t1?.always_on_doc_count || 0} of {t1?.doc_count || 0} pinned · the rest searched
            {t1?.indexed_chunks ? ` across ${fmtChars(t1.indexed_chunks)} passages` : ""}
            {" · "}all of Tier 1 would cost {fmtCost(t1?.full_injection_cost_cents_per_ask)}/answer
          </div>
        </div>
      ) : usage && (
        <div className="mb-3" data-testid={`tier-${tier}-usage`}>
          <div className="flex justify-between text-[10px] text-[#6B6A68] mb-1">
            <span>{fmtKB(used)} of {fmtKB(cap)} used <span className="opacity-60">({fmtChars(used)} chars)</span></span>
            <span style={{ color: meterColor }}>{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#F0F1EE] overflow-hidden">
            <div className="h-full" style={{ width: `${pct}%`, background: meterColor, transition: "width 200ms" }} />
          </div>
        </div>
      )}

      {/* One-time index build. Tier 1 content written before Oct 6 2026 was
          never embedded, so Core Library has nothing to search until this runs.
          Shown whenever coverage is incomplete, in either mode — the index is
          maintained in both. */}
      {tier === 1 && (t1?.unindexed_doc_count > 0 || reindexJob?.status === "running") && (
        <div className="mb-3 rounded-md border hairline bg-[#FCF6E6] px-2.5 py-2" data-testid="tier-1-reindex-card">
          {reindexJob?.status === "running" ? (
            <div className="text-[10px] text-[#8A6D1F]" data-testid="tier-1-reindex-progress">
              Building Tier 1 index… {reindexJob.done || 0} of {reindexJob.total || 0} files · {fmtChars(reindexJob.chunks)} passages
            </div>
          ) : (
            <>
              <div className="text-[10px] text-[#8A6D1F] leading-snug mb-1.5">
                {t1.unindexed_doc_count} Tier 1 file{t1.unindexed_doc_count === 1 ? "" : "s"} not indexed yet.
                Build it either way — it is free, runs on this server and costs nothing in AI.
                Only Core Library searches the index; Full Injection never does.
              </div>
              <button
                type="button"
                onClick={onReindex}
                data-testid="tier-1-reindex-btn"
                className="text-[10px] px-2 py-1 rounded border border-[#C19A4A] bg-white text-[#8A6D1F] hover:bg-[#FCF6E6]"
              >
                Build Tier 1 index
              </button>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mt-auto">
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`tier-${tier}-upload`}
          className="text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center justify-center gap-1.5 disabled:opacity-50">
          <UploadSimple size={14} weight="duotone" /> Upload
        </button>
        <button onClick={() => setPasting((p) => !p)} disabled={busy} data-testid={`tier-${tier}-paste-toggle`}
          className="text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center justify-center gap-1.5 disabled:opacity-50">
          <TextT size={14} weight="duotone" /> Paste
        </button>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" className="hidden"
          onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`tier-${tier}-file-input`} />
      </div>

      {/* Oct 7 2026 — rights attestation + keep-your-own-copy, AT THE POINT OF
          UPLOAD. Both facts live in the Terms already, but a clause agreed to
          months ago at signup is far weaker than a statement sitting where the
          action happens — and this is the surface where someone would paste
          somebody else's book. Deliberately a standing line, not a per-upload
          checkbox: uploading happens dozens of times a session and a modal
          would train people to click through it. */}
      <p className="mt-2 text-[10px] text-[#6B6A68] leading-snug" data-testid={`tier-${tier}-rights-note`}>
        By adding content you confirm you hold the rights to it. Keep your own copy of anything you
        can&apos;t afford to lose — see{" "}
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms</a>.
      </p>

      {pasting && (
        <div className="mt-3 space-y-2">
          <input value={pasteTitle} onChange={(e) => setPasteTitle(e.target.value)} placeholder="Title (optional)"
            data-testid={`tier-${tier}-paste-title`}
            className="w-full bg-[#FDFCF8] border hairline rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
          <textarea value={pasteContent} onChange={(e) => setPasteContent(e.target.value)} rows={5}
            data-testid={`tier-${tier}-paste-content`}
            className="w-full bg-[#FDFCF8] border hairline rounded-md px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            placeholder={`Paste a passage destined for ${meta.label}…`} />
          <div className="flex items-center justify-between text-[10px] text-[#6B6A68]">
            <span>
              {fmtChars(pasteContent.length)} chars · {fmtKB(new Blob([pasteContent]).size)}
              {pasteOversized && (
                <span className="ml-2 text-[#C16E5A]" data-testid={`tier-${tier}-oversize`}>
                  · over the {meta.perDocKB} KB per-doc cap — try Refinery or Workbench first
                </span>
              )}
            </span>
            <span className="opacity-70">cap: {meta.perDocKB} KB</span>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setPasting(false); setPasteTitle(""); setPasteContent(""); }}
              className="text-xs px-3 py-1.5 text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
            <button onClick={submitPaste} disabled={busy || !pasteContent.trim() || pasteOversized} data-testid={`tier-${tier}-paste-submit`}
              className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-60">Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Library({ siloId, siloName, onSwitchTab, active = true }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmState, setConfirmState] = useState({ open: false });
  const [moveState, setMoveState] = useState({ open: false, doc: null });
  const [renameState, setRenameState] = useState({ open: false, doc: null });
  const [editorState, setEditorState] = useState({ open: false, doc: null });
  const [silos, setSilos] = useState([]);
  // Feb 24, 2026 — per-tier usage from /docs/usage. Drives the
  // "267 KB of 1.0 MB used" capacity meter on each tier zone.
  const [usage, setUsage] = useState(null);
  // Oct 6 2026 (Randy pt-tier1-two-mode) — Tier 1 mode + Always On pins +
  // the one-time index backfill. All three read from /docs/usage.
  const [modeBusy, setModeBusy] = useState(false);
  const [reindexJob, setReindexJob] = useState(null);
  const t1 = usage?.tier1 || null;
  const coreMode = t1?.mode === "core_library";

  const refreshUsage = async () => {
    try {
      const r = await api.get(`/silos/${siloId}/docs/usage`);
      setUsage(r.data);
    } catch { /* leave the last-known numbers on screen */ }
  };

  const changeTier1Mode = async (mode) => {
    setModeBusy(true);
    try {
      await api.patch(`/silos/${siloId}`, { tier1_mode: mode });
      await refreshUsage();
      toast.success(mode === "core_library"
        ? "Tier 1 is now a searchable Core Library. Check the files you want in every answer."
        : "Tier 1 is back to Full Injection — every file is sent with every question.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change Tier 1 mode.");
    } finally {
      setModeBusy(false);
    }
  };

  // One tap pins the WHOLE document, at any size. No threshold, no excerpt
  // workflow — Randy's ruling Oct 6 2026: the numbers educate, they don't gate.
  const toggleAlwaysOn = async (doc) => {
    const next = !(doc.always_on === true);
    setDocs((arr) => arr.map((d) => (d.id === doc.id ? { ...d, always_on: next } : d)));
    try {
      await api.patch(`/silos/${siloId}/docs/${doc.id}/always-on`, { always_on: next });
      await refreshUsage();
    } catch (e) {
      setDocs((arr) => arr.map((d) => (d.id === doc.id ? { ...d, always_on: !next } : d)));
      toast.error(e.response?.data?.detail || "Could not change that pin.");
    }
  };

  const startReindex = async () => {
    try {
      const r = await api.post(`/silos/${siloId}/tier1/reindex`);
      setReindexJob(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start indexing.");
    }
  };

  useEffect(() => {
    if (reindexJob?.status !== "running") return;
    const id = setInterval(async () => {
      try {
        const r = await api.get(`/silos/${siloId}/tier1/reindex`);
        setReindexJob(r.data);
        if (r.data?.status !== "running") {
          clearInterval(id);
          await refreshUsage();
          if (r.data?.status === "done") toast.success("Tier 1 index built.");
          if (r.data?.status === "error") toast.error("Indexing stopped early. Try again.");
        }
      } catch { /* transient — keep polling */ }
    }, 1500);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [reindexJob?.status, siloId]);
  // iter-35d: each tier section is independently collapsible. Default OPEN
  // so existing users don't lose visibility on first load. Persisted in
  // sessionStorage scoped to (siloId, tier) so a creator's preference survives
  // tab switches and page reloads inside the same session.
  const [tierOpen, setTierOpen] = useState(() => {
    try {
      const raw = sessionStorage.getItem(`library_tier_open_${siloId}`);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === "object") return { 1: parsed["1"] !== false, 2: parsed["2"] !== false, 3: parsed["3"] !== false };
    } catch { /* ignore */ }
    return { 1: true, 2: true, 3: true };
  });
  useEffect(() => {
    try { sessionStorage.setItem(`library_tier_open_${siloId}`, JSON.stringify(tierOpen)); }
    catch { /* ignore */ }
  }, [tierOpen, siloId]);
  const toggleTier = (t) => setTierOpen((s) => ({ ...s, [t]: !s[t] }));

  // Jul 2026 — whole-silo content finder. Type a word/phrase; the backend
  // (free, instant, no AI) returns every doc whose title OR content contains it
  // (exact, case-insensitive), plus per-tier counts. We light up each matching
  // file's tier icon. The lit state lives here (not in the editor), so it
  // survives opening & closing a file with the pencil.
  const [searchQ, setSearchQ] = useState("");
  const [searchHits, setSearchHits] = useState(null); // { ids:Set, by_tier:{}, total }
  const [searching, setSearching] = useState(false);
  const searchActive = !!searchQ.trim();
  useEffect(() => {
    const q = searchQ.trim();
    if (!q) { setSearchHits(null); setSearching(false); return; }
    setSearching(true);
    const id = setTimeout(async () => {
      try {
        const r = await api.get(`/silos/${siloId}/docs/search`, { params: { q } });
        setSearchHits({ ids: new Set(r.data?.doc_ids || []), by_tier: r.data?.by_tier || {}, total: r.data?.total || 0 });
      } catch { setSearchHits(null); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(id);
  }, [searchQ, siloId]);

  const load = async () => {
    setLoading(true);
    try {
      const [docsRes, silosRes, usageRes, jobRes] = await Promise.all([
        api.get(`/silos/${siloId}/docs`),
        api.get("/silos"),
        api.get(`/silos/${siloId}/docs/usage`).catch(() => ({ data: null })),
        api.get(`/silos/${siloId}/tier1/reindex`).catch(() => ({ data: null })),
      ]);
      setDocs(docsRes.data); setSilos(silosRes.data); setUsage(usageRes.data);
      setReindexJob(jobRes.data);
    } catch { toast.error("Failed to load library"); } finally { setLoading(false); }
  };
  useEffect(() => { if (active) load(); /* eslint-disable-next-line */ }, [active, siloId]);

  const upload = async (file, tier) => {
    if (!file) return;
    setSubmitting(true);
    const form = new FormData();
    form.append("file", file); form.append("title", file.name); form.append("tier", String(tier));
    try {
      await api.post(`/silos/${siloId}/docs/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Added "${file.name}" to Tier ${tier}.`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setSubmitting(false); }
  };

  const paste = async ({ title, content }, tier) => {
    setSubmitting(true);
    try {
      await api.post(`/silos/${siloId}/docs/paste`, { title, content, tier });
      toast.success(`Passage added to Tier ${tier}.`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSubmitting(false); }
  };

  const remove = (id, title) => {
    setConfirmState({
      open: true, title: `Remove "${title}"?`, message: "Permanently removes this document.",
      danger: true, confirmLabel: "Remove",
      onConfirm: async () => {
        try {
          await api.delete(`/silos/${siloId}/docs/${id}`);
          setDocs((d) => d.filter((x) => x.id !== id));
          toast.success("Removed.");
        } catch { toast.error("Delete failed"); }
      },
    });
  };

  // Sprint Feb 7, 2026 — flip a doc's enabled flag. Excluded from retrieval
  // when off, but still stored. Optimistic update — revert on failure.
  // Sep 29, 2026 (Randy pt-voice-kit) — the tap now CYCLES three stops:
  // normal → off (red, excluded from answers) → green (100% user written).
  // Green is deliberately inert for the silo: it changes nothing about how the
  // file feeds answers. It only tells the Voice Kit which files it may pull a
  // random writing swatch from.
  const cycleDocState = async (doc) => {
    if (doc?.locked === true) { setLockNote(true); return; }
    const isOff = doc.enabled === false;
    const isGreen = doc.voice_source === true;
    const before = { enabled: doc.enabled !== false, voice_source: isGreen };
    let next;
    if (isGreen) next = { enabled: true, voice_source: false };
    else if (isOff) next = { enabled: true, voice_source: true };
    else next = { enabled: false, voice_source: false };
    setDocs((arr) => arr.map((d) => (d.id === doc.id ? { ...d, ...next } : d)));
    try {
      if (next.enabled !== before.enabled) {
        await api.patch(`/silos/${siloId}/docs/${doc.id}/enabled`, { enabled: next.enabled });
      }
      if (next.voice_source !== before.voice_source) {
        await api.patch(`/silos/${siloId}/docs/${doc.id}/voice-source`, { voice_source: next.voice_source });
      }
      if (next.voice_source) toast.success(`"${doc.title}" marked 100% your writing — the Voice Kit can pull writing samples from it.`);
      else if (!next.enabled) toast.success(`"${doc.title}" is off — kept but excluded from answers.`);
      else toast.success(`"${doc.title}" is on.`);
    } catch (e) {
      setDocs((arr) => arr.map((d) => (d.id === doc.id ? { ...d, ...before } : d)));
      toast.error(e.response?.data?.detail || "Could not change file state.");
    }
  };

  // Download URL is computed inline below on each `<a>` tag. NO JS-click synthesis
  // — browsers (especially hosting preview iframes) block programmatic `.click()`.
  // The ONLY reliable path is a real anchor the user clicks directly; that click is
  // a first-party user gesture, and the backend sends `Content-Disposition: attachment`
  // so the browser handles "Save to Downloads" without us touching the DOM.
  const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const downloadUrlFor = (doc) => {
    // The auth token is stored under "mysilo_token" (see lib/api.js). Earlier code
    // used the wrong key, which produced URLs like "?token=" with an empty value
    // and the backend rejected them with 401, breaking downloads on Mac/desktop.
    const token = sessionStorage.getItem("mysilo_token") || "";
    return `${BACKEND_BASE}/api/silos/${siloId}/docs/${doc.id}/download?token=${encodeURIComponent(token)}`;
  };
  const safeFilename = (doc) =>
    `${(doc.title || "document").replace(/[^a-z0-9-_]+/gi, "-")}.txt`;

  // Oct 2026 (Randy) — TOOL-BUILT FILE LOCK. A file the Workbench or Refinery
  // produced is unreadable while the owner doesn't hold Minimum Creator Pro:
  // the backend 403s every read path, so this just explains why. Deleting is
  // still allowed — it's their silo — and their own uploads are never locked.
  const [lockNote, setLockNote] = useState(false);
  const { openGate } = useCreatorProGate();
  // Art off → the original cards. Stage 1 or 2 → the painted ones.
  const { mode: artMode } = useSiloArt();
  const artOn = artMode === "v1" || artMode === "v2";
  const blockedByLock = (doc) => {
    if (doc?.locked !== true) return false;
    setLockNote(true);
    return true;
  };

  const openMove = (doc) => { if (blockedByLock(doc)) return; setMoveState({ open: true, doc }); };
  const openEditor = (doc) => { if (blockedByLock(doc)) return; setEditorState({ open: true, doc }); };

  const openRename = (doc) => { if (blockedByLock(doc)) return; setRenameState({ open: true, doc }); };
  const submitRename = async (newTitle) => {
    const doc = renameState.doc;
    if (!doc || !newTitle?.trim()) return setRenameState({ open: false, doc: null });
    try {
      const r = await api.patch(`/silos/${siloId}/docs/${doc.id}/rename`, { title: newTitle.trim() });
      // Optimistic update + full refetch so the row repaints immediately AND the
      // tier-grouping (`groupedByTier`) recomputes with the new title.
      setDocs((d) => d.map((x) => x.id === doc.id ? { ...x, title: r.data.title } : x));
      toast.success(`Renamed to "${r.data.title}".`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    } finally {
      setRenameState({ open: false, doc: null });
    }
  };
  const handleMove = async ({ tier, silo_id, keep_original }) => {
    const doc = moveState.doc;
    if (!doc || !silo_id || !tier) return;
    try {
      await api.patch(`/silos/${siloId}/docs/${doc.id}/move`, {
        target_silo_id: silo_id, tier, keep_original: !!keep_original,
      });
      toast.success(keep_original ? "Copied." : "Moved.");
      setMoveState({ open: false, doc: null });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Move failed"); }
  };

  const sendToWorkbench = async (doc) => {
    if (blockedByLock(doc)) return;
    try {
      const r = await api.get(`/silos/${siloId}/docs/${doc.id}/raw`);
      const header = `--- ${doc.title || "Document"} ---\n`;
      sessionStorage.setItem("workbench_seed", JSON.stringify({
        staging: true, append: true, title: doc.title, content: header + r.data.content,
      }));
      toast.success(`"${doc.title}" appended to staging area.`);
      onSwitchTab?.("workbench");
    } catch { toast.error("Could not stage"); }
  };

  const groupedByTier = { 1: [], 2: [], 3: [] };
  for (const d of docs) groupedByTier[d.tier || 3].push(d);

  return (
    <div data-testid="library-tab">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Books size={20} weight="duotone" className="text-[#4A5D4E]" />
          <h2 className="font-serif-display text-3xl text-[#1C1B1A]">The Library</h2>
        </div>
        <p className="text-sm text-[#6B6A68] max-w-3xl leading-relaxed">
          Every word your AI twin knows lives here. Three tiers, total isolation, instant retrieval. Drop in PDFs, paste passages,
          watch them indexed in milliseconds — Tier 1 becomes the soul, Tier 2 the working memory, Tier 3 the deep archive.
          This is where your voice becomes a database.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TierZone tier={1} busy={submitting} onUpload={(f) => upload(f, 1)} onPaste={(p) => paste(p, 1)} usage={usage?.tiers?.["1"]}
          t1={t1} onModeChange={changeTier1Mode} modeBusy={modeBusy} onReindex={startReindex} reindexJob={reindexJob} artOn={artOn} />
        <TierZone tier={2} busy={submitting} onUpload={(f) => upload(f, 2)} onPaste={(p) => paste(p, 2)} usage={usage?.tiers?.["2"]} artOn={artOn} />
        <TierZone tier={3} busy={submitting} onUpload={(f) => upload(f, 3)} onPaste={(p) => paste(p, 3)} usage={usage?.tiers?.["3"]} artOn={artOn} />
      </div>

      {/* Jul 2026 — whole-silo content finder. Lights up the tier icon of every
          file that contains the word/phrase (exact, case-insensitive). */}
      <div className="mt-10" data-testid="library-search">
        <div className="relative max-w-2xl">
          <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A68] pointer-events-none" />
          <input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Find a word or phrase anywhere in this silo…"
            data-testid="library-search-input"
            className="w-full bg-white border hairline rounded-md pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
          />
          {searchQ && (
            <button onClick={() => setSearchQ("")} data-testid="library-search-clear" title="Clear search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-[#6B6A68] hover:text-[#C16E5A]">
              <X size={15} />
            </button>
          )}
        </div>
        {searchActive && (
          <div className="mt-2 text-xs text-[#6B6A68]" data-testid="library-search-summary">
            {searching ? "Searching…" : (searchHits?.total > 0 ? (
              <>Lit up <span className="text-[#8A6D1F] font-medium">{searchHits.total}</span> file{searchHits.total === 1 ? "" : "s"}
                {" · "}Tier 1 ({searchHits.by_tier?.["1"] || 0}) · Tier 2 ({searchHits.by_tier?.["2"] || 0}) · Tier 3 ({searchHits.by_tier?.["3"] || 0})</>
            ) : (
              <>No files contain “{searchQ.trim()}”.</>
            ))}
          </div>
        )}
      </div>

      <section className="mt-6 space-y-8">
        {[1, 2, 3].map((t) => {
          const Icon = TIER_META[t].icon;
          const hitCount = searchActive ? (searchHits?.by_tier?.[String(t)] || 0) : null;
          const open = (searchActive && hitCount > 0) ? true : !!tierOpen[t];
          const docCount = groupedByTier[t].length;
          const charCount = groupedByTier[t].reduce((s, d) => s + (d.char_count || 0), 0);
          return (
            <div key={t} data-testid={`tier-section-${t}`}>
              {/* iter-35d: tier header is a button that toggles the doc list.
                  Long libraries no longer dominate the page; creators can fold
                  any tier they're not actively curating.

                  Iter160m (Randy pt-siloart-loosetext-sweep) — the tier
                  header text is visually LOOSE (sits on the canvas, no
                  card behind it), even though it's inside a link-styled
                  button. `.silo-loose` opts the H3 + span into the Silo
                  Art V2 whitetext/blacktext rules despite the structural
                  button exclusion. */}
              <button
                type="button"
                onClick={() => toggleTier(t)}
                aria-expanded={open}
                data-testid={`tier-toggle-${t}`}
                className="w-full flex items-center gap-2 mb-3 text-left hover:opacity-80 transition-opacity"
              >
                {open
                  ? <CaretDown size={14} weight="bold" className="text-[#6B6A68] shrink-0" />
                  : <CaretRight size={14} weight="bold" className="text-[#6B6A68] shrink-0" />}
                <Icon size={18} weight="duotone" style={{ color: TIER_META[t].accent }} />
                <h3 className="font-serif-display text-xl text-[#1C1B1A] silo-loose">{TIER_META[t].label}</h3>
                <span className="text-xs text-[#6B6A68] silo-loose">· {docCount} doc{docCount === 1 ? "" : "s"} · {charCount.toLocaleString()} chars</span>
                {searchActive && (
                  <span className={`text-xs font-medium ${hitCount > 0 ? "text-[#8A6D1F]" : "text-[#6B6A68]"}`} data-testid={`tier-match-count-${t}`}>
                    · {hitCount} match{hitCount === 1 ? "" : "es"}
                  </span>
                )}
              </button>
              {open && (loading ? <div className="text-sm text-[#6B6A68]">Loading…</div> :
                groupedByTier[t].length === 0 ? (
                  <div className="bg-white border hairline rounded-lg p-6 text-center text-[#6B6A68] text-sm">No documents in this tier yet.</div>
                ) : (
                  <ul className="divide-y hairline bg-white border hairline rounded-lg" data-testid={`tier-list-${t}`}>
                    {groupedByTier[t].map((d) => {
                      const isOff = d.enabled === false;
                      const isGreen = d.voice_source === true;
                      const isLocked = d.locked === true;
                      const isHit = !!searchHits?.ids?.has(d.id);
                      return (
                      <li key={d.id} className={`p-4 flex flex-col sm:flex-row sm:items-start gap-3 ${isOff ? "opacity-70" : ""} ${isLocked ? "bg-[#FDF1EE]" : ""} ${isHit ? "bg-[#FCF6E6]" : ""}`} data-testid={`doc-row-${d.id}`} data-hit={isHit ? "true" : "false"} data-locked={isLocked ? "true" : "false"}>
                        {/* Top: toggle icon + name/meta. On mobile takes FULL width
                            (actions drop below); on desktop it's the flex-1 left side. */}
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                        {/* Sprint Feb 7, 2026 — file icon is now an active
                            toggle. Tap to flip enabled/disabled. Light-red
                            background when disabled (file excluded from
                            retrieval but still stored). Jul 2026 — a golden
                            glow lights the icon when the file matches a search.
                            Sep 29, 2026 — a third tap turns it GREEN: "100%
                            my writing", which only marks it as sample-eligible
                            for the Voice Kit and changes nothing else. */}
                        <button
                          onClick={() => cycleDocState(d)}
                          title={
                            isLocked
                              ? "Locked — built by the Workbench or Refinery. Needs Minimum Creator Pro."
                              : isGreen
                                ? "Green — 100% your writing. The Voice Kit may pull writing samples from this file. Tap to clear."
                                : isOff
                                  ? "Off — excluded from answers. Tap to mark it 100% your writing (green)."
                                  : "On. Tap to turn it off (kept, but excluded from answers)."
                          }
                          data-testid={`doc-toggle-${d.id}`}
                          data-state={isLocked ? "locked" : isGreen ? "green" : isOff ? "off" : "on"}
                          className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 transition-all ${
                            isLocked
                              ? "bg-[#F7D9D1] text-[#A33B22] hover:bg-[#F2C9BE]"
                              : isGreen
                                ? "bg-[#DCEEDF] text-[#2F6B3A] hover:bg-[#CBE6D0]"
                                : isOff
                                  ? "bg-[#FDE3DB] text-[#C16E5A] hover:bg-[#FCD2C5]"
                                  : "bg-[#F0F1EE] text-[#4A5D4E] hover:bg-[#E5E5E0]"
                          } ${isHit ? "ring-2 ring-[#E0A63C] ring-offset-1 shadow-[0_0_10px_2px_rgba(224,166,60,0.45)]" : ""}`}
                        >
                          {isLocked
                            ? <LockKey size={16} weight="duotone" />
                            : d.source_type === "upload" ? <FileText size={16} weight="duotone" /> : <TextT size={16} weight="duotone" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium break-words sm:truncate ${isLocked ? "text-[#A33B22] line-through" : isOff ? "text-[#6B6A68] line-through" : "text-[#1C1B1A]"}`}>
                            {d.title}
                            {isLocked && <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#A33B22] no-underline" data-testid={`doc-locked-tag-${d.id}`}>Locked</span>}
                            {isOff && <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#C16E5A] no-underline" data-testid={`doc-disabled-tag-${d.id}`}>Off</span>}
                            {isGreen && <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#2F6B3A] no-underline" data-testid={`doc-voice-tag-${d.id}`}>100% mine</span>}
                            {t === 1 && coreMode && d.always_on === true && (
                              <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#4A5D4E] no-underline" data-testid={`doc-always-on-tag-${d.id}`}>
                                Always On · {(d.char_count || 0).toLocaleString()}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[#6B6A68] mt-0.5">
                            {d.source_type === "upload" ? `File · ${d.filename || ""}` : "Pasted"} · {d.char_count.toLocaleString()} chars · {formatDate(d.created_at)}
                          </div>
                        </div>
                        </div>
                        {/* Actions: mobile = full-width row below (spread + own top
                            border); desktop = inline to the right (original layout). */}
                        <div className="flex items-center gap-1 justify-between sm:justify-start border-t hairline sm:border-t-0 pt-3 sm:pt-0 mt-1 sm:mt-0 shrink-0" data-testid={`doc-actions-${d.id}`}>
                        {/* Oct 6 2026 (Randy pt-tier1-two-mode) — ALWAYS ON pin.
                            Appears only on Tier 1 and only in Core Library mode,
                            as the leading control in the existing cluster. One
                            tap pins the whole file whatever its size. */}
                        {t === 1 && coreMode && (
                          <button
                            onClick={() => toggleAlwaysOn(d)}
                            title={d.always_on === true
                              ? `Always On — this whole file (${(d.char_count || 0).toLocaleString()} chars) is injected into every answer. Tap to unpin and let it be searched instead.`
                              : `Tap to pin this whole file (${(d.char_count || 0).toLocaleString()} chars) into every answer. Unpinned files are searched.`}
                            data-testid={`doc-always-on-${d.id}`}
                            data-state={d.always_on === true ? "on" : "off"}
                            className={`p-1.5 rounded-md ${d.always_on === true ? "text-[#4A5D4E] bg-[#EDF2EE]" : "text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]"}`}
                          >
                            {d.always_on === true
                              ? <CheckSquare size={16} weight="fill" />
                              : <Square size={16} weight="regular" />}
                          </button>
                        )}
                        <button onClick={() => openEditor(d)} title="Open &amp; edit" data-testid={`doc-edit-${d.id}`}
                          className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]"><PencilSimple size={16} weight="duotone" /></button>
                        {isLocked ? (
                          <button onClick={() => setLockNote(true)} title="Locked — needs Minimum Creator Pro"
                            data-testid={`doc-download-${d.id}`}
                            className="p-1.5 rounded-md text-[#A33B22] hover:bg-[#F7D9D1] flex items-center justify-center">
                            <DownloadSimple size={16} weight="duotone" />
                          </button>
                        ) : (
                          <a href={downloadUrlFor(d)} download={safeFilename(d)}
                            target="_blank" rel="noopener noreferrer"
                            title="Download .txt" data-testid={`doc-download-${d.id}`}
                            className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE] flex items-center justify-center">
                            <DownloadSimple size={16} weight="duotone" />
                          </a>
                        )}
                        <button onClick={() => openRename(d)} title="Rename" data-testid={`doc-rename-${d.id}`}
                          className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]"><TextAa size={16} weight="duotone" /></button>
                        <button onClick={() => openMove(d)} title="Move to silo / tier" data-testid={`doc-move-${d.id}`}
                          className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]"><ArrowsClockwise size={16} weight="duotone" /></button>
                        <button onClick={() => sendToWorkbench(d)} title="Append to Workbench staging area" data-testid={`doc-workbench-${d.id}`}
                          className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]"><Wrench size={16} weight="duotone" /></button>
                        <button onClick={() => remove(d.id, d.title)} title="Remove" data-testid={`library-delete-${d.id}`}
                          className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#F0F1EE]"><Trash size={16} weight="duotone" /></button>
                        </div>
                      </li>
                      );
                    })}
                  </ul>
                ))}
            </div>
          );
        })}
      </section>

      <ConfirmModal {...confirmState} onClose={() => setConfirmState({ open: false })} />
      <PromptModal
        open={renameState.open}
        title="Rename document"
        label="New title"
        defaultValue={renameState.doc?.title || ""}
        placeholder="e.g., Foundational essay on grace"
        submitLabel="Rename"
        onSubmit={submitRename}
        onClose={() => setRenameState({ open: false, doc: null })}
      />
      <TierPickerModal
        open={moveState.open}
        onClose={() => setMoveState({ open: false, doc: null })}
        onSubmit={handleMove}
        title="Move document"
        subtitle={moveState.doc?.title}
        showTitleField={false}
        showSiloField={true}
        showKeepOriginal={true}
        silos={silos}
        defaultSiloId={siloId}
        initialTier={moveState.doc?.tier || 2}
        contentLength={moveState.doc?.char_count || 0}
        submitLabel="Apply"
      />
      <DocEditorModal
        open={editorState.open}
        onClose={() => setEditorState({ open: false, doc: null })}
        siloId={siloId}
        doc={editorState.doc}
        silos={silos}
        docs={docs}
        onSaved={load}
        onSwitchTab={onSwitchTab}
      />
      {/* Oct 2026 (Randy) — the tool-built file lock, explained. Kept to two
          sentences on purpose: the second one is doing the real work, because
          "they locked my data" and "they locked their software's output" are
          very different accusations. */}
      {lockNote && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
             onClick={() => setLockNote(false)} data-testid="tool-lock-modal">
          <div className="absolute inset-0 bg-[#1C1B1A]/45 backdrop-blur-sm" />
          <div className="relative z-10 bg-white rounded-2xl max-w-sm w-full p-7 shadow-2xl text-center"
               onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-[#F7D9D1] text-[#A33B22] flex items-center justify-center mx-auto">
              <LockKey size={22} weight="duotone" />
            </div>
            <h3 className="font-serif-display text-xl text-[#1C1B1A] mt-4">This file is locked.</h3>
            <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
              Workbench and Refinery files need Minimum Creator Pro. Your originals are untouched.
            </p>
            <div className="flex flex-col gap-2 mt-6">
              <button
                onClick={() => { setLockNote(false); openGate("min_workbench"); }}
                data-testid="tool-lock-unlock-btn"
                className="w-full px-5 py-3 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] transition-colors text-sm font-medium"
              >
                Unlock Minimum Creator Pro
              </button>
              <button
                onClick={() => setLockNote(false)}
                data-testid="tool-lock-close-btn"
                className="w-full text-xs text-[#6B6A68] hover:text-[#1C1B1A] py-2"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// iter-35f: shared tier-card tooltip — top-centered above the (i) icon,
// grace-period delay-close to eliminate flicker. Mirrors InfoTip's behavior.
function TierTooltip({ text, testid, onArt = false }) {
  const [open, setOpen] = useState(false);
  // Jun 2026 — the Tier 1 copy is long and these cards sit near the top of the
  // page, so a popup anchored above the icon ran off-screen. Flip it under the
  // icon when there isn't room above.
  const [below, setBelow] = useState(false);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);
  const scheduleOpen = () => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    const r = wrapRef.current?.getBoundingClientRect();
    setBelow(!!r && r.top < window.innerHeight - r.bottom);
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => { setOpen(false); closeTimerRef.current = null; }, 180);
  };
  useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }, []);
  return (
    <span
      ref={wrapRef}
      className="relative inline-flex cursor-help"
      data-testid={testid}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocus={scheduleOpen}
      onBlur={scheduleClose}
    >
      <button
        type="button"
        aria-label="Tier info"
        aria-expanded={open}
        className="focus:outline-none"
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
      >
        <Info size={16} weight="duotone" className={onArt ? "text-white/80" : "text-[#4A5D4E]"} />
      </button>
      {open && (
        <span
          role="tooltip"
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
          className={`pointer-events-auto absolute z-[90] left-1/2 -translate-x-1/2 bg-[#1C1B1A] text-[#FDFCF8] text-xs leading-relaxed rounded-md p-3 w-64 shadow-2xl whitespace-normal text-left ${below ? "top-full mt-2" : "bottom-full mb-2"}`}
        >
          {text}
        </span>
      )}
    </span>
  );
}
